import React, { useState, useEffect } from 'react';
import { CheckCircle, Ban, ShieldCheck, KeyRound, Trash2, Settings2 } from 'lucide-react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminsList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    adminId: null,
    action: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [adminsPerPage] = useState(10);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [password, setPassword] = useState('');
  const [isFeaturesModalOpen, setIsFeaturesModalOpen] = useState(false);
  const [selectedOrgFeatures, setSelectedOrgFeatures] = useState({ organizationId: null, livechat: true, tickets: true });


  // Fetch admins on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/getAdminUserList`);
      console.log('Admins data:', response.data); // Add logging to check data structure
      setAdmins(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to load admins');
      setLoading(false);
    }
  };

  const handleStatusChange = async (adminId, action) => {
    try {
      console.log(`Updating admin ${adminId} with action: ${action}`); // Debug log
      
      // Fix: Send the request in the format the backend expects
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/updateAdminStatus`, { 
        adminId: adminId, 
        action: action 
      });
      
      console.log('Update response:', response.data); // Debug log
      
      // Update local state without requiring a refetch
      setAdmins(prevAdmins => 
        prevAdmins.map(admin => {
          if (admin.id === adminId) {
            console.log(`Updating admin in state: ${admin.id}`); // Debug log
            return { 
              ...admin,
              status: action // Just use the action value directly since it's already the status value
            };
          }
          return admin;
        })
      );
      
      const actionText = action === '1' ? 'approved' : action === '2' ? 'blocked' : 'updated';
      toast.success(`Admin ${actionText} successfully`);
      setConfirmDialog({ ...confirmDialog, open: false });
    } catch (error) {
      console.error(`Error updating admin status:`, error);
      toast.error(`Failed to update admin: ${error.message}`);
    }
  };

  const openConfirmDialog = (adminId, action) => {
    console.log(`Opening dialog for admin ${adminId}, action: ${action}`); // Debug log
    
    let title, message;
    if (action === '1') {
      title = 'Approve Admin';
      message = 'Are you sure you want to approve this admin? They will have access to the admin dashboard.';
    } else if (action === '2') {
      title = 'Block Admin';
      message = 'Are you sure you want to block this admin? They will lose access to the admin dashboard.';
    }

    setConfirmDialog({
      open: true,
      title,
      message,
      adminId,
      action,
    });
  };

  const getStatusBadgeClass = (status) => {
    // Convert numeric status to string representation
    if (status === 1 || status === '1') {
      return 'bg-green-100 text-green-800'; // active
    } else if (status === 0 || status === '0') {
      return 'bg-yellow-100 text-yellow-800'; // pending
    } else if (status === 2 || status === '2') {
      return 'bg-red-100 text-red-800'; // blocked
    } else {
      return 'bg-gray-100 text-gray-800'; // default
    }
  };

  const getStatusText = (status) => {
    // Convert numeric status to display text
    if (status === 1 || status === '1') {
      return 'Active';
    } else if (status === 0 || status === '0') {
      return 'Pending';
    } else if (status === 2 || status === '2') {
      return 'Blocked';
    } else {
      return 'Unknown';
    }
  };

  const handleLoginAsAdmin = async (adminId) => {
    try {
      console.log('Attempting to login as admin ID:', adminId); // Debug log

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/loginAsAdmin`, {
        adminId: adminId // Sending just the ID
      });

      if (response.data) {
        const orgName = response.data.organization_name;
        
        // Store the admin session data temporarily
        const tempKey = btoa(Date.now());
        sessionStorage.setItem("organizationName", orgName);
        sessionStorage.setItem(`${orgName}_token`, response.data.token);
        sessionStorage.setItem(`${orgName}_adminId`, response.data.adminId);
        sessionStorage.setItem(`${orgName}_organizationId`, response.data.organization_id);
        sessionStorage.setItem(`${orgName}_role`, response.data.role);

        window.open(`/${orgName}/home`, '_blank');
        
        setTimeout(() => {
          sessionStorage.removeItem('temp_admin_session');
        }, 5000);
      }
    } catch (error) {
      console.error('Error logging in as admin:', error);
      toast.error('Failed to login as admin');
    }
  };

  // Get current admins for pagination
  const indexOfLastAdmin = currentPage * adminsPerPage;
  const indexOfFirstAdmin = indexOfLastAdmin - adminsPerPage;
  const currentAdmins = admins.slice(indexOfFirstAdmin, indexOfLastAdmin);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Go to previous page
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Go to next page
  const goToNextPage = () => {
    if (currentPage < Math.ceil(admins.length / adminsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const openResetModal = (adminId) => {
    setSelectedAdminId(adminId);
    setPassword('');
    setIsResetModalOpen(true);
  };

  const handleDeleteAdmin = async (adminId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/deleteAdmin/${adminId}`);
      setAdmins(prev => prev.filter(admin => admin.id !== adminId));
      toast.success('Admin deleted successfully.');
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('Failed to delete admin.');
    }
  };

  const openFeaturesModal = (admin) => {
    const features = admin.features || 'livechat,tickets';
    setSelectedOrgFeatures({
      organizationId: admin.organization_id,
      livechat: features.includes('livechat'),
      tickets: features.includes('tickets'),
    });
    setIsFeaturesModalOpen(true);
  };

  const handleSaveFeatures = async () => {
    const parts = [];
    if (selectedOrgFeatures.livechat) parts.push('livechat');
    if (selectedOrgFeatures.tickets) parts.push('tickets');
    const features = parts.join(',') || 'tickets';
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/updateOrgFeatures`, {
        organizationId: selectedOrgFeatures.organizationId,
        features,
      });
      setAdmins(prev => prev.map(a =>
        a.organization_id === selectedOrgFeatures.organizationId ? { ...a, features } : a
      ));
      toast.success('Features updated successfully.');
      setIsFeaturesModalOpen(false);
    } catch (error) {
      console.error('Error updating features:', error);
      toast.error('Failed to update features.');
    }
  };

  const openDeleteDialog = (adminId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Admin',
      message: 'Are you sure you want to permanently delete this admin? This action cannot be undone.',
      adminId,
      action: 'delete',
    });
  };

  const handleResetPassword = async (adminId) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/updateAdminPassword`, { password, adminId });
      if (response.status === 200) {
        toast.success('Password reset successfully.');
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    }
  }

  return (
    <div className="container bg-gray-100 mx-auto px-4 py-8 rounded-lg">
      <ToastContainer autoClose={5000} pauseOnHover />
      <h1 className="text-2xl font-bold mb-6">Admins Management</h1>
      
      {loading ? (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Password
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Features
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentAdmins.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No admins found
                    </td>
                  </tr>
                ) : (
                  currentAdmins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {admin.organization_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {admin.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {admin.password}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(admin.status)}`}>
                          {getStatusText(admin.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(admin.created_at || admin.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-1 flex-wrap">
                          {admin.features?.includes('livechat') && (
                            <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs">Live Chat</span>
                          )}
                          {admin.features?.includes('tickets') && (
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs">Tickets</span>
                          )}
                          {!admin.features && (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {(admin.status === 0 || admin.status === '0') && (
                            <button
                              title="Approve"
                              className="p-1.5 rounded bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => openConfirmDialog(admin.id, '1')}
                            >
                              <CheckCircle size={15} />
                            </button>
                          )}

                          {(admin.status === 0 || admin.status === '0' || admin.status === 1 || admin.status === '1') && (
                            <button
                              title="Block"
                              className="p-1.5 rounded bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => openConfirmDialog(admin.id, '2')}
                            >
                              <Ban size={15} />
                            </button>
                          )}

                          {(admin.status === 2 || admin.status === '2') && (
                            <button
                              title="Unblock"
                              className="p-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => openConfirmDialog(admin.id, '1')}
                            >
                              <ShieldCheck size={15} />
                            </button>
                          )}

                          <button
                            title="Reset Password"
                            className="p-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => openResetModal(admin.id)}
                          >
                            <KeyRound size={15} />
                          </button>

                          <button
                            title="Edit Features"
                            className="p-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => openFeaturesModal(admin)}
                          >
                            <Settings2 size={15} />
                          </button>
                          <button
                            title="Delete"
                            className="p-1.5 rounded bg-red-700 hover:bg-red-800 text-white"
                            onClick={() => openDeleteDialog(admin.id)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {admins.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstAdmin + 1}</span> to{" "}
                  <span className="font-medium">
                    {indexOfLastAdmin > admins.length ? admins.length : indexOfLastAdmin}
                  </span>{" "}
                  of <span className="font-medium">{admins.length}</span> admins
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, Math.ceil(admins.length / adminsPerPage)) }).map((_, idx) => {
                    // Calculate page number based on current page to show 5 page numbers at a time
                    let pageNum;
                    const totalPages = Math.ceil(admins.length / adminsPerPage);
                    
                    if (totalPages <= 5) {
                      // If total pages is 5 or less, just show page numbers 1 through totalPages
                      pageNum = idx + 1;
                    } else {
                      // For more pages, create a sliding window
                      if (currentPage <= 3) {
                        // At the beginning, show first 5 pages
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        // At the end, show last 5 pages
                        pageNum = totalPages - 4 + idx;
                      } else {
                        // In the middle, center around current page
                        pageNum = currentPage - 2 + idx;
                      }
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === Math.ceil(admins.length / adminsPerPage)}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === Math.ceil(admins.length / adminsPerPage)
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-gray-500 mb-4">{confirmDialog.message}</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  confirmDialog.action === '1'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                onClick={() => {
                  if (confirmDialog.action === 'delete') {
                    handleDeleteAdmin(confirmDialog.adminId);
                    setConfirmDialog({ ...confirmDialog, open: false });
                  } else {
                    handleStatusChange(confirmDialog.adminId, confirmDialog.action);
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {isFeaturesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h2 className="text-xl font-semibold mb-4">Edit Features</h2>
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedOrgFeatures.livechat}
                  onChange={(e) => setSelectedOrgFeatures({ ...selectedOrgFeatures, livechat: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-700">Live Chat</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedOrgFeatures.tickets}
                  onChange={(e) => setSelectedOrgFeatures({ ...selectedOrgFeatures, tickets: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-700">Ticket System</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsFeaturesModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFeatures}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Reset Password</h2>
            <input
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
            />
            <div className="flex justify-end">
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="mr-3 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleResetPassword(selectedAdminId);
                  setIsResetModalOpen(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsList;
